/**
 * Product analytics event definitions (PostHog schema v2.1).
 *
 * Every client-side product event is fired through a typed function in this
 * module. Nothing else in the app should call `posthog.capture` directly for
 * a schema event — event names and property shapes live here so a typo is a
 * compile error rather than a silently empty insight.
 *
 * Identity, the tenant group and the `environment` super property are all set
 * up in ./posthog.ts and apply automatically to everything captured here.
 *
 * Money is pounds to 2 decimal places with `currency` alongside it, on every
 * event that carries a monetary value. Optional properties are sent as explicit `null`
 * rather than omitted, so the property exists in PostHog's schema and can be
 * filtered on.
 */
import { posthog } from './posthog'
import type { CartItemResponse } from '../auth/services/cart-api'
import type { ProductAddMode } from '../constants/quickAdd'
import { PRODUCT_ADD_MODES } from '../constants/quickAdd'

/**
 * Capture an event, swallowing any failure.
 *
 * Analytics must never be able to break the flow it is measuring: these calls
 * sit inside checkout and cart effects, where a thrown error would surface as
 * a broken page rather than a missing datapoint. Mirrors the backend, where
 * common/posthog_client.capture() is likewise incapable of raising.
 *
 * @param event Event name from ANALYTICS_EVENTS.
 * @param properties Event properties.
 * @param options Optional posthog capture options (e.g. sendBeacon transport).
 */
function safeCapture(
  event: string,
  properties: Record<string, unknown>,
  options?: Parameters<typeof posthog.capture>[2],
): void {
  try {
    posthog.capture(event, properties, options)
  } catch {
    // Never surface an analytics failure to the engineer.
  }
}

/** Event names — must match the server-side constants in common/analytics.py. */
export const ANALYTICS_EVENTS = {
  USER_LOGGED_IN: 'user_logged_in',
  SEARCH_PERFORMED: 'search_performed',
  PRODUCT_VIEWED: 'product_viewed',
  BASKET_ITEM_ADDED: 'basket_item_added',
  BASKET_QUANTITY_UPDATED: 'basket_quantity_updated',
  BASKET_ITEM_REMOVED: 'basket_item_removed',
  BASKET_CLEARED: 'basket_cleared',
  CHECKOUT_STARTED: 'checkout_started',
  SEARCH_RESULT_CLICKED: 'search_result_clicked',
  SUPPLIER_BRANCH_FILTER_APPLIED: 'supplier_branch_filter_applied',
  PRODUCT_ADDED_TO_BASKET_MODE: 'product_added_to_basket_mode',
} as const

/** The only currency the platform supports today (schema dependency D6). */
export const CURRENCY_GBP = 'GBP'

/** Where the engineer opened a product from. */
export const ProductViewSource = {
  SEARCH: 'search',
  BROWSE: 'browse',
  FAVOURITE: 'favourite',
  DIRECT: 'direct',
} as const

export type ProductViewSource =
  (typeof ProductViewSource)[keyof typeof ProductViewSource]

/**
 * Round a pound amount to 2 decimal places for reporting.
 *
 * Sent as pounds rather than integer pence so anyone building an insight reads
 * the real figure without a conversion step.
 *
 * @param pounds Amount in pounds, or null when unknown.
 * @returns The amount to 2dp, or null when the input is null/not finite.
 */
export function toAmount(pounds: number | null | undefined): number | null {
  if (pounds === null || pounds === undefined || !Number.isFinite(pounds)) {
    return null
  }
  // Rounded to the penny so the value never carries more precision than the
  // money actually has — a basket total summed from per-unit prices can
  // otherwise land on something like 45.239999999999995.
  return Math.round(pounds * 100) / 100
}

interface UserLoggedInPayload {
  role: string
  /** Computed by the backend and exposed on /api/users/me — the client cannot derive it. */
  isFirstLogin: boolean
}

interface SearchPerformedPayload {
  /**
   * Correlation id for this search, shared with every search_result_clicked
   * it produces. Minted client-side once per distinct search (query +
   * category) and reused across pagination and the product-detail
   * round-trip, so a click traces to the exact search — not just to a
   * keyword that may have been searched more than once.
   */
  searchId: string
  query: string
  resultsCount: number
  /**
   * Comma-separated distinct supplier names of the "Show first" branches in
   * force for this search (DEV-50739), or null when none applied.
   */
  supplierFilters: string | null
  supplierFilterCount: number
  /**
   * Comma-separated "Supplier · Branch" labels of those same branches, or
   * null when none applied. One supplier can contribute several branches.
   */
  branchFilters: string | null
  branchFilterCount: number
  /** Comma-separated category names, or null when none applied. */
  categoryFilters: string | null
  categoryFilterCount: number
}

/** Which kind of product detail page was opened. */
export const ProductViewType = {
  MASTER: 'master',
  RAW: 'raw',
} as const

export type ProductViewType =
  (typeof ProductViewType)[keyof typeof ProductViewType]

/**
 * Resolve where a product-detail open came from, from React Router state.
 *
 * Only Search and Favourites tag their navigations. Everything else — a cart
 * line, a product tile, recently-purchased, a pasted link, a reload — arrives
 * with no state, which is exactly `direct`.
 *
 * @param state The `location.state` on the product page.
 * @returns The matching source, defaulting to `direct`.
 */
export function resolveProductViewSource(state: unknown): ProductViewSource {
  const source =
    typeof state === 'object' && state !== null
      ? (state as Record<string, unknown>).source
      : undefined
  if (
    source === ProductViewSource.SEARCH ||
    source === ProductViewSource.FAVOURITE ||
    source === ProductViewSource.BROWSE
  ) {
    return source
  }
  return ProductViewSource.DIRECT
}

/**
 * Forget the source once a view has been recorded.
 *
 * React Router keeps location.state in history.state, so it is restored on a
 * page reload — but a reload is not a fresh open from search or favourites, it
 * is a direct visit. Clearing the stored source (without a navigation, so no
 * re-render) means the reload resolves to `direct`. No-op when history is
 * unavailable.
 */
export function clearProductViewSource(): void {
  try {
    const existing =
      typeof history.state === 'object' && history.state !== null
        ? (history.state as Record<string, unknown>)
        : {}
    history.replaceState({ ...existing, usr: null }, '')
  } catch {
    // History unavailable — nothing to clear.
  }
}

interface ProductViewedPayload {
  type: ProductViewType
  /** All raw/supplier fields are null on a master-product view. */
  rawProductId: string | null
  rawProductName: string | null
  masterProductId: string | null
  masterProductName: string | null
  supplier: string | null
  supplierId: string | null
  source: ProductViewSource
}

interface BasketItemAddedPayload {
  rawProductId: string
  rawProductName: string
  supplier: string
  supplierId: string
  branchId: string
  branchName: string
  quantity: number
  /** Engineer's working postcode, as stored on the cart. */
  postcode: string | null
  isVanStock: boolean
  /** Stock at the selected branch when the item was added. */
  inStockAtBranch: boolean | null
}

interface BasketQuantityUpdatedPayload {
  rawProductId: string
  rawProductName: string
  supplier: string
  supplierId: string
  branchId: string
  branchName: string
  previousQuantity: number
  newQuantity: number
  /** Positive on an increase, negative on a decrease. */
  delta: number
  /** Line value before the change. */
  previousValue: number | null
  /** Line value after the change. */
  newValue: number | null
  /** Money added to or taken out of the basket by this change. */
  valueDelta: number | null
}

interface ProductAddedToBasketModePayload {
  mode: ProductAddMode
  rawProductId: string
  rawProductName: string
  supplier: string
  supplierId: string
  branchId: string
  branchName: string
  /** How many this add put into the basket. */
  quantityAdded: number
  /**
   * Quick add only: the card's place among that supplier's cards in the
   * search list (1 = its first). Null for a standard add.
   */
  productPosition: number | null
}

interface BasketItemRemovedPayload {
  rawProductId: string
  rawProductName: string
  supplier: string
  supplierId: string
  /** The quantity the line held when it was removed. */
  quantityRemoved: number
}

/** Collection or delivery, as selected on the checkout page. */
export const FulfillmentMode = {
  PICKUP: 'pickup',
  DELIVERY: 'delivery',
} as const

export type FulfillmentMode = (typeof FulfillmentMode)[keyof typeof FulfillmentMode]

interface CheckoutStartedPayload {
  checkoutId: string
  itemCount: number
  /**
   * Distinct suppliers in the basket. Computed, not assumed: checkout is
   * entered per branch group today so this is always 1, but deriving it means
   * the property self-corrects if multi-supplier checkout ever returns.
   */
  supplierCount: number
  /** Checkout is scoped to a single branch, so one supplier per checkout. */
  supplier: string
  supplierId: string
  branchId: string | null
  branchName: string
  fulfillmentMode: FulfillmentMode
  /** Engineer's session postcode, for location-based analysis. */
  postcode: string | null
  /** Estimate — prices are refreshed again before submission. */
  basketValue: number | null
}

interface SearchResultClickedPayload {
  /** The search_performed this click belongs to — see SearchPerformedPayload. */
  searchId: string
  /** Search results are master products, so there is no single raw product
   * or supplier at click time — the engineer picks those on the next page. */
  masterProductId: string
  masterProductName: string
  /** 1-indexed position on the current page. */
  resultPosition: number
  /** Which page of results the click was on. */
  page: number
  query: string
}

interface SupplierBranchFilterAppliedPayload {
  /**
   * The search_performed this change belongs to — see SearchPerformedPayload.
   * Null on a category-only browse, which emits no search_performed.
   */
  searchId: string | null
  /** Comma-separated ids / names of every branch ticked after the change. */
  supplierIds: string
  supplierNames: string
  branchIds: string
  branchNames: string
  /** Branches ticked after the change. */
  selectedCount: number
  /** The committed search term; empty string on a category browse. */
  query: string
  /** Whole search result count (all pages). */
  totalResultsCount: number
  /**
   * Size of the "Showing first" group for this selection — results any
   * ticked branch can supply. 0 means the engineer sees "No matches". Null
   * when the search moved on (or the page closed) before every stock answer
   * was in.
   */
  priorityResultsCount: number | null
  postcode: string | null
}

/**
 * Fires once per authenticated session, after identifyUser() has run.
 *
 * Callers must guard this — /api/users/me resolves on every hard refresh, so
 * an unguarded call would report a new login on every page reload.
 */
export function trackUserLoggedIn(payload: UserLoggedInPayload): void {
  safeCapture(ANALYTICS_EVENTS.USER_LOGGED_IN, {
    role: payload.role,
    is_first_login: payload.isFirstLogin,
  })
}

/**
 * Fires on every settled search submission, including a re-search caused by
 * changing a filter.
 *
 * Must fire when `resultsCount` is 0 — zero-result searches are the
 * catalogue-gap signal. Only an empty or whitespace query is suppressed, and
 * that suppression belongs to the caller.
 */
export function trackSearchPerformed(payload: SearchPerformedPayload): void {
  safeCapture(ANALYTICS_EVENTS.SEARCH_PERFORMED, {
    search_id: payload.searchId,
    query: payload.query,
    results_count: payload.resultsCount,
    supplier_filters: payload.supplierFilters,
    supplier_filter_count: payload.supplierFilterCount,
    branch_filters: payload.branchFilters,
    branch_filter_count: payload.branchFilterCount,
    category_filters: payload.categoryFilters,
    category_filter_count: payload.categoryFilterCount,
  })
}

/**
 * Fires once per product-detail open.
 *
 * Both the master and the supplier (raw) detail pages fire this; `type` says
 * which, and the raw/supplier fields are null on a master view because the
 * engineer has not chosen a supplier yet. Callers guard against re-firing on
 * scroll or tab switch — those do not remount the page.
 */
export function trackProductViewed(payload: ProductViewedPayload): void {
  safeCapture(ANALYTICS_EVENTS.PRODUCT_VIEWED, {
    type: payload.type,
    raw_product_id: payload.rawProductId,
    raw_product_name: payload.rawProductName,
    master_product_id: payload.masterProductId,
    master_product_name: payload.masterProductName,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    source: payload.source,
  })
}

/**
 * Fires only when a NEW line (a product at a branch) enters the basket — not
 * on quantity changes, and not when an existing line is moved to another
 * branch. Quick add from search keeps a line per branch, so adding a product
 * already in the basket at another branch counts here as a new line.
 *
 * The callers already enforce this: they look the product up in the cart and
 * route an existing line to updateCartLine (which changes quantity, branch, or
 * both), reaching the add mutation only for a product the basket does not
 * hold. So this event needs no "is it already there" check of its own.
 */
export function trackBasketItemAdded(payload: BasketItemAddedPayload): void {
  safeCapture(ANALYTICS_EVENTS.BASKET_ITEM_ADDED, {
    raw_product_id: payload.rawProductId,
    raw_product_name: payload.rawProductName,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    branch_id: payload.branchId,
    branch_name: payload.branchName,
    quantity: payload.quantity,
    postcode: payload.postcode,
    is_van_stock: payload.isVanStock,
    in_stock_at_branch: payload.inStockAtBranch,
  })
}

/**
 * Fires when the quantity of an item already in the basket changes, in either
 * direction.
 *
 * Not fired for a first add (that is basket_item_added) or a removal (that is
 * basket_item_removed) — both take different mutations. The caller is
 * responsible for only calling this when the quantity actually moved: the same
 * update mutation also carries van-stock toggles and branch moves.
 */
export function trackBasketQuantityUpdated(
  payload: BasketQuantityUpdatedPayload,
): void {
  safeCapture(ANALYTICS_EVENTS.BASKET_QUANTITY_UPDATED, {
    raw_product_id: payload.rawProductId,
    raw_product_name: payload.rawProductName,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    branch_id: payload.branchId,
    branch_name: payload.branchName,
    previous_quantity: payload.previousQuantity,
    new_quantity: payload.newQuantity,
    delta: payload.delta,
    previous_value: payload.previousValue,
    new_value: payload.newValue,
    value_delta: payload.valueDelta,
    currency: CURRENCY_GBP,
  })
}

/**
 * Fires only when a line leaves the basket entirely.
 *
 * A quantity decrease is basket_quantity_updated, not this — the two use
 * different mutations, so neither can fire for the other.
 */
export function trackBasketItemRemoved(payload: BasketItemRemovedPayload): void {
  safeCapture(ANALYTICS_EVENTS.BASKET_ITEM_REMOVED, {
    raw_product_id: payload.rawProductId,
    raw_product_name: payload.rawProductName,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    quantity_removed: payload.quantityRemoved,
  })
}

/**
 * Fires when an engineer adds a product, saying how: quick add from a search
 * card, or the standard Add / Update on a product page (only when it raises
 * the quantity). Lets quick-add use be compared with normal adds, and shows
 * how far down a supplier's cards quick adds happen. Separate from the
 * basket events, which are unchanged.
 */
export function trackProductAddedToBasketMode(
  payload: ProductAddedToBasketModePayload,
): void {
  safeCapture(ANALYTICS_EVENTS.PRODUCT_ADDED_TO_BASKET_MODE, {
    mode: payload.mode,
    raw_product_id: payload.rawProductId,
    raw_product_name: payload.rawProductName,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    branch_id: payload.branchId,
    branch_name: payload.branchName,
    quantity_added: payload.quantityAdded,
    product_position: payload.productPosition,
  })
}

/**
 * The standard (product page) add of the product-added-mode event, built from
 * the basket line the server returned. Callers send it only when the add
 * raised the quantity.
 *
 * @param line - The created or updated basket line.
 * @param quantityAdded - How many the add put in (the increase on an update).
 */
export function trackStandardProductAdd(
  line: CartItemResponse,
  quantityAdded: number,
): void {
  trackProductAddedToBasketMode({
    mode: PRODUCT_ADD_MODES.standard,
    rawProductId: String(line.product_id),
    rawProductName: line.product_name,
    supplier: line.supplier_name,
    supplierId: String(line.supplier_id),
    branchId: String(line.branch_id),
    branchName: line.branch_name,
    quantityAdded,
    productPosition: null,
  })
}

interface BasketClearedPayload {
  /** Distinct product lines the basket held when cleared. */
  itemCount: number
  /** Total basket value at clear, in pounds to 2dp. */
  basketValue: number | null
}

/**
 * Fires when the engineer clears the whole basket in one action ("Clear Cart").
 *
 * Distinct from basket_item_removed: that is one line; this is dumping the
 * lot, which reads as a restart or frustration signal. Only the deliberate
 * Clear Cart action fires it — never the backend clearing the cart after a
 * successful order, and never removing the last line individually.
 */
export function trackBasketCleared(payload: BasketClearedPayload): void {
  safeCapture(ANALYTICS_EVENTS.BASKET_CLEARED, {
    item_count: payload.itemCount,
    basket_value: payload.basketValue,
    currency: CURRENCY_GBP,
  })
}

/**
 * Fires once per navigation to the checkout page.
 *
 * The tenant group is attached automatically — posthog.group('tenant', ...)
 * runs at identify time and applies to every subsequent event.
 */
export function trackCheckoutStarted(payload: CheckoutStartedPayload): void {
  safeCapture(ANALYTICS_EVENTS.CHECKOUT_STARTED, {
    checkout_id: payload.checkoutId,
    item_count: payload.itemCount,
    supplier_count: payload.supplierCount,
    supplier: payload.supplier,
    supplier_id: payload.supplierId,
    branch_id: payload.branchId,
    branch_name: payload.branchName,
    fulfillment_mode: payload.fulfillmentMode,
    postcode: payload.postcode,
    basket_value: payload.basketValue,
    currency: CURRENCY_GBP,
  })
}

/**
 * Fires every time an engineer opens a product from the search results list.
 *
 * On every click, not just the first: which positions get opened is the
 * signal. result_position is 1-indexed.
 */
/**
 * Fires once per change to the "Show first" selection — a pill tap or a
 * sheet Apply — while at least one branch remains ticked (DEV-50739).
 * Describes the whole selection after the change, not the single pill
 * touched; unticking the last pill fires nothing. The caller sends it once
 * every page and stock answer for the search has settled, so
 * priority_results_count is complete.
 */
export function trackSupplierBranchFilterApplied(
  payload: SupplierBranchFilterAppliedPayload,
): void {
  safeCapture(ANALYTICS_EVENTS.SUPPLIER_BRANCH_FILTER_APPLIED, {
    search_id: payload.searchId,
    supplier_ids: payload.supplierIds,
    supplier_names: payload.supplierNames,
    branch_ids: payload.branchIds,
    branch_names: payload.branchNames,
    selected_count: payload.selectedCount,
    query: payload.query,
    total_results_count: payload.totalResultsCount,
    priority_results_count: payload.priorityResultsCount,
    postcode: payload.postcode,
  })
}

export function trackSearchResultClicked(payload: SearchResultClickedPayload): void {
  safeCapture(ANALYTICS_EVENTS.SEARCH_RESULT_CLICKED, {
    search_id: payload.searchId,
    master_product_id: payload.masterProductId,
    master_product_name: payload.masterProductName,
    result_position: payload.resultPosition,
    page: payload.page,
    query: payload.query,
  })
}
